// Shared types + design tokens for the Schedule calendar (kept out of the
// component file so fast-refresh works — see react-refresh/only-export-components).

import type { InsuranceVerdict } from '@/hooks/useInsuranceVerdicts'
import { INSURANCE_STATUS_LABELS } from '@/utils/insuranceVerdict'
import {
  IconAlertTriangle,
  IconCheck,
  IconHourglassEmpty,
  IconThumbUp,
  IconVideo,
} from '@tabler/icons-react'
import type { SelfPayStatus } from '@/model/middleware-types'

export interface CalendarEvent {
  id: string
  /** 'appointment_slot' for encounters, 'available_slot' for availability bands. */
  type?: string
  title: string
  start: string
  end: string
  display?: string
  backgroundColor?: string
  appointmentId?: string
  appointmentStatus?: string
  practitionerId?: string
  /** Visit reason display text (Encounter.reasonCode[0].text), shown as a badge
   *  in the appointment preview popup. */
  reasonLabel?: string
  patientId?: string
  /** Patient.birthDate, resolved with the schedule load (one batched
   *  `_elements=birthDate` search) so the detail preview can render DOB/age
   *  immediately instead of waiting on its own patient read. */
  patientBirthDate?: string
  /**
   * Zocdoc's own appointment id, when this visit is bound to a Zocdoc booking
   * (`Appointment.identifier` under `ZOCDOC_APPOINTMENT_SYSTEM`). Its presence *is* the link —
   * read off the Appointment the schedule already fetches, so the calendar costs no extra call
   * to know. Absent for a visit booked only in beHuman.
   */
  zocdocAppointmentId?: string
  /**
   * Self-pay fee status (`Appointment.extension` self-pay-status), read off the Appointment the
   * schedule already fetches. Absent for insured visits.
   */
  selfPayStatus?: SelfPayStatus
  /** For availability (background) events: the FHIR Slot status. */
  slotType?: string
}

/** Availability band fills for single-practitioner view (FHIR Slot status → color). */
export const AVAILABILITY_BG: Record<string, string> = {
  free: 'rgba(34,197,94,0.13)', // green — open/available
  'busy-unavailable': 'rgba(239,68,68,0.11)', // red — blocked
  busy: 'rgba(234,179,8,0.14)', // amber — busy (status updated)
}

/** When slots overlap, the higher priority wins so one 30-min cell shows a
 * single color (a blocked/busy slot never blends with free underneath, which
 * looked like a stray yellow bleeding into a neighbouring slot). */
export const AVAILABILITY_PRIORITY: Record<string, number> = {
  'busy-unavailable': 3,
  busy: 2,
  free: 1,
}

export const APPOINTMENT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending Confirmation' },
  { value: 'booked', label: 'Confirmed' },
  { value: 'checked-in', label: 'Patient Ready' },
  { value: 'noshow', label: 'No Show' },
  { value: 'fulfilled', label: 'Completed' },
] as const

export type AppointmentStatusValue = (typeof APPOINTMENT_STATUS_OPTIONS)[number]['value']

// Appointment/insurance palettes (SCHEDULE_V2_DESIGN.md).
export const APPOINTMENT_STATUS_CHIP: Record<string, { color: string; label: string }> = {
  pending: { color: '#3B82F6', label: 'Pending' },
  booked: { color: '#16A34A', label: 'Confirmed' },
  'checked-in': { color: '#16A34A', label: 'Patient Ready' },
  fulfilled: { color: '#94A3B8', label: 'Completed' },
  noshow: { color: '#EF4444', label: 'No-show' },
}

/** Appointment status → icon (legacy mapping), shared by the cards and the
 * status filter so the same glyph carries the same meaning. */
export const APPOINTMENT_STATUS_ICON: Record<string, typeof IconCheck> = {
  pending: IconHourglassEmpty,
  booked: IconThumbUp,
  'checked-in': IconVideo,
  noshow: IconAlertTriangle,
  fulfilled: IconCheck,
}

// The insurance verdict's roll-up (insuranceVerdicts), the same words as the provider app.
export const INSURANCE_STATUS_CHIP: Record<
  InsuranceVerdict['insuranceStatus'],
  { color: string; label: string }
> = {
  verified: { color: '#16A34A', label: INSURANCE_STATUS_LABELS.verified },
  'review-required': { color: '#F59E0B', label: INSURANCE_STATUS_LABELS['review-required'] },
  none: { color: '#94A3B8', label: INSURANCE_STATUS_LABELS.none },
}

/** Time as e.g. "2:00pm" — shared by every appointment block. */
export const fmtTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase()

// Grid time window (Week/Day views): cards render only between 6am and 7pm.
// Shared with the filter counts so "All" reflects exactly what the grid shows.
export const DAY_START = 6
export const DAY_END = 19

/**
 * Event-strip height, which also drives the Week/Day slot-row height (a row is
 * `rows × strip height`) and the Month cells' strips — the two grids must stay
 * in step or the same appointment changes size between views.
 *
 * The BIG variant applies on desktop-Mac-class displays (see `useBigScreen`),
 * where the 13-hour grid at the default height leaves a large empty band under
 * the calendar: 26 slots × 26px ≈ 676px of grid, vs ≈ 884px at 34px.
 */
export const STRIP_HEIGHT = 26
export const STRIP_HEIGHT_BIG = 34

export const sameLocalDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()

/** The day columns a view renders from its visible range: a single day (Day
 * view, kept even on a weekend) or the weekdays (Mon–Fri) of the range. */
export const getVisibleDays = (range: { start: Date; end: Date }): Date[] => {
  const all: Date[] = []
  const cursor = new Date(range.start)
  cursor.setHours(0, 0, 0, 0)
  const end = new Date(range.end)
  while (cursor < end) {
    all.push(new Date(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }
  if (all.length <= 1) return all
  const weekdays = all.filter((d) => d.getDay() >= 1 && d.getDay() <= 5)
  return weekdays.length ? weekdays : all
}

/** Whether an event actually renders in the grid: not a background band, falls
 * on a visible day column, and (Week/Day only) inside the 6am–7pm window. Used
 * both to lay cards out and to count them, so the two never diverge. */
export const isEventRendered = (
  event: CalendarEvent,
  days: Date[],
  clipToDayHours: boolean,
): boolean => {
  if (event.display === 'background' || !event.start) return false
  const d = new Date(event.start)
  if (!days.some((day) => sameLocalDay(day, d))) return false
  if (clipToDayHours) {
    const slot = d.getHours() + (d.getMinutes() >= 30 ? 0.5 : 0)
    if (slot < DAY_START || slot >= DAY_END) return false
  }
  return true
}

/**
 * Encounter events the segment filters leave on the grid. Availability bands are
 * never filtered — they are the backdrop, not appointments.
 *
 * Insurance and Returning Patient each read a map that lands a request AFTER the
 * events are painted, and an appointment missing from its map fails the test. So
 * each of those predicates only applies once its own map exists: with the filters
 * now restored on arrival rather than always starting at `all`, applying one
 * against a map that has not resolved yet would empty the grid and assert "no
 * matching appointments" from data the page never had (PD-1206).
 */
export function filterVisibleEvents(
  events: CalendarEvent[],
  filters: {
    statusFilters: readonly string[]
    insuranceFilter: 'all' | 'verified' | 'review-required'
    returningFilter: boolean
    insuranceVerdictMap?: Record<string, InsuranceVerdict>
    returningPatientMap?: Record<string, boolean>
  },
): CalendarEvent[] {
  const {
    statusFilters,
    insuranceFilter,
    returningFilter,
    insuranceVerdictMap,
    returningPatientMap,
  } = filters
  const insuranceApplies = insuranceFilter !== 'all' && Boolean(insuranceVerdictMap)
  const returningApplies = returningFilter && Boolean(returningPatientMap)

  if (statusFilters.length === 0 && !insuranceApplies && !returningApplies) return events

  return events.filter((event) => {
    if (event.type !== 'appointment_slot') return true
    if (statusFilters.length > 0 && !statusFilters.includes(event.appointmentStatus as string)) {
      return false
    }
    if (insuranceApplies) {
      if (insuranceVerdictMap?.[event.id]?.insuranceStatus !== insuranceFilter) return false
    }
    if (returningApplies && !returningPatientMap?.[event.id]) return false
    return true
  })
}
